import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getItemReportByDateRangeOnline,
    getItemReportHourlyOnline,
    getItemReportTaxwiseOnline,
    getItemReportTopBottomSellingOnline,
    formatDateForApi
} from '../../../utilities/api';

export const ITEM_ONLINE_REPORT_TABS = {
    FULL: 0,
    HOURLY: 1,
    TAXWISE: 2,
    PERFORMANCE: 3
};

const buildPayload = ({ fromDate, toDate, branchId }) => {
    const resolvedBranchId = branchId || 'All';
    const payload = {
        from_date: fromDate ? formatDateForApi(fromDate) : '',
        to_date: toDate ? formatDateForApi(toDate) : '',
        branch_id: resolvedBranchId
    };
    if (String(resolvedBranchId).toLowerCase() === 'all') {
        const clientId = localStorage.getItem('clientid');
        if (clientId) payload.client_id = clientId;
    }
    return payload;
};

export const fetchItemOnlineFullReport = createAsyncThunk(
    'itemOnlineReport/fetchItemOnlineFullReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getItemReportByDateRangeOnline(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) return rejectWithValue(res.error);
            return {
                bills: res?.bills || [],
                summary: res?.summary || {},
                status: res?.status
            };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch item online full report');
        }
    }
);

export const fetchItemOnlineHourlyReport = createAsyncThunk(
    'itemOnlineReport/fetchItemOnlineHourlyReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getItemReportHourlyOnline(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) return rejectWithValue(res.error);
            return res?.data && typeof res.data === 'object' ? res.data : {};
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch item online hourly report');
        }
    }
);

export const fetchItemOnlineTaxwiseReport = createAsyncThunk(
    'itemOnlineReport/fetchItemOnlineTaxwiseReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getItemReportTaxwiseOnline(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) return rejectWithValue(res.error);
            return Array.isArray(res?.data) ? res.data : [];
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch item online tax-wise report');
        }
    }
);

export const fetchItemOnlinePerformanceReport = createAsyncThunk(
    'itemOnlineReport/fetchItemOnlinePerformanceReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getItemReportTopBottomSellingOnline(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) return rejectWithValue(res.error);
            return {
                report_period: res?.report_period || null,
                top_selling: Array.isArray(res?.top_selling) ? res.top_selling : [],
                bottom_selling: Array.isArray(res?.bottom_selling) ? res.bottom_selling : []
            };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch item online performance report');
        }
    }
);

const initialState = {
    selectedBranch: null,
    fromDate: null,
    toDate: null,
    activeTab: ITEM_ONLINE_REPORT_TABS.FULL,

    fullReport: { bills: [], summary: {} },
    hourlyReport: {},
    taxwiseReport: [],
    performanceReport: { report_period: null, top_selling: [], bottom_selling: [] },

    loading: { full: false, hourly: false, taxwise: false, performance: false },
    error: { full: null, hourly: null, taxwise: null, performance: null },
    lastQuery: { full: null, hourly: null, taxwise: null, performance: null }
};

const itemOnlineReportSlice = createSlice({
    name: 'itemOnlineReport',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.activeTab = ITEM_ONLINE_REPORT_TABS.FULL;
            state.fullReport = { bills: [], summary: {} };
            state.hourlyReport = {};
            state.taxwiseReport = [];
            state.performanceReport = { report_period: null, top_selling: [], bottom_selling: [] };
            state.loading = { full: false, hourly: false, taxwise: false, performance: false };
            state.error = { full: null, hourly: null, taxwise: null, performance: null };
            state.lastQuery = { full: null, hourly: null, taxwise: null, performance: null };
        },
        clearErrors: (state) => {
            state.error = { full: null, hourly: null, taxwise: null, performance: null };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchItemOnlineFullReport.pending, (state) => {
                state.loading.full = true;
                state.error.full = null;
            })
            .addCase(fetchItemOnlineFullReport.fulfilled, (state, action) => {
                state.loading.full = false;
                state.fullReport = { bills: action.payload?.bills || [], summary: action.payload?.summary || {} };
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.full = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemOnlineFullReport.rejected, (state, action) => {
                state.loading.full = false;
                state.error.full = action.payload;
                state.fullReport = { bills: [], summary: {} };
            })
            .addCase(fetchItemOnlineHourlyReport.pending, (state) => {
                state.loading.hourly = true;
                state.error.hourly = null;
            })
            .addCase(fetchItemOnlineHourlyReport.fulfilled, (state, action) => {
                state.loading.hourly = false;
                state.hourlyReport = action.payload != null && typeof action.payload === 'object' ? action.payload : {};
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.hourly = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemOnlineHourlyReport.rejected, (state, action) => {
                state.loading.hourly = false;
                state.error.hourly = action.payload;
                state.hourlyReport = {};
            })
            .addCase(fetchItemOnlineTaxwiseReport.pending, (state) => {
                state.loading.taxwise = true;
                state.error.taxwise = null;
            })
            .addCase(fetchItemOnlineTaxwiseReport.fulfilled, (state, action) => {
                state.loading.taxwise = false;
                state.taxwiseReport = Array.isArray(action.payload) ? action.payload : [];
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.taxwise = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemOnlineTaxwiseReport.rejected, (state, action) => {
                state.loading.taxwise = false;
                state.error.taxwise = action.payload;
                state.taxwiseReport = [];
            })
            .addCase(fetchItemOnlinePerformanceReport.pending, (state) => {
                state.loading.performance = true;
                state.error.performance = null;
            })
            .addCase(fetchItemOnlinePerformanceReport.fulfilled, (state, action) => {
                state.loading.performance = false;
                state.performanceReport = action.payload || { report_period: null, top_selling: [], bottom_selling: [] };
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.performance = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchItemOnlinePerformanceReport.rejected, (state, action) => {
                state.loading.performance = false;
                state.error.performance = action.payload;
                state.performanceReport = { report_period: null, top_selling: [], bottom_selling: [] };
            });
    }
});

export const { setFromDate, setToDate, setSelectedBranch, setActiveTab, resetFilters, clearErrors } = itemOnlineReportSlice.actions;

export const selectItemOnlineReport = (state) => state.itemOnlineReport;
export const selectActiveTab = (state) => state.itemOnlineReport.activeTab;
export const selectSelectedBranch = (state) => state.itemOnlineReport.selectedBranch;
export const selectFromDate = (state) => state.itemOnlineReport.fromDate;
export const selectToDate = (state) => state.itemOnlineReport.toDate;
export const selectFullReport = (state) => state.itemOnlineReport.fullReport;
export const selectHourlyReport = (state) => state.itemOnlineReport.hourlyReport;
export const selectTaxwiseReport = (state) => state.itemOnlineReport.taxwiseReport;
export const selectPerformanceReport = (state) => state.itemOnlineReport.performanceReport;
export const selectItemHourlyData = (state) => state.itemOnlineReport.hourlyReport;

export const selectActiveLoading = (state) => {
    const tab = state.itemOnlineReport.activeTab;
    const l = state.itemOnlineReport.loading;
    if (tab === ITEM_ONLINE_REPORT_TABS.FULL) return l.full;
    if (tab === ITEM_ONLINE_REPORT_TABS.HOURLY) return l.hourly;
    if (tab === ITEM_ONLINE_REPORT_TABS.TAXWISE) return l.taxwise;
    if (tab === ITEM_ONLINE_REPORT_TABS.PERFORMANCE) return l.performance;
    return false;
};

export const selectActiveError = (state) => {
    const tab = state.itemOnlineReport.activeTab;
    const e = state.itemOnlineReport.error;
    if (tab === ITEM_ONLINE_REPORT_TABS.FULL) return e.full;
    if (tab === ITEM_ONLINE_REPORT_TABS.HOURLY) return e.hourly;
    if (tab === ITEM_ONLINE_REPORT_TABS.TAXWISE) return e.taxwise;
    if (tab === ITEM_ONLINE_REPORT_TABS.PERFORMANCE) return e.performance;
    return null;
};

export default itemOnlineReportSlice.reducer;
